import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';

import Iconify from 'src/components/iconify';
import TransactionNotFound from 'src/features/transactions/TransactionNotFound';
import { formatAmount } from 'src/utils/formatNumber';
import {
  STATUS_CONFIG,
  STEP_ORDER,
  getConfirmationProgress,
  getStepIndexFromStatus,
  isFailedStatus,
  isIncomingTxFresh,
} from 'src/constants/transactionStatus';
import { config as globalConfig } from 'src/config-global';
import { AuthContext } from 'src/auth/context';
import { fetchTransactionById } from 'src/redux/slices/transactions-slice';
import { getAdminSocket, getClientSocket } from 'src/utils/socket';
import toast from 'react-hot-toast';
import { formatAddress } from 'src/utils/crypto/formatAddress';
import { downloadReceiptPdf } from 'src/utils/receipt/downloadReceiptPdf';

// GLITCH FIXES (vs previous version):
// - The page used to prefer a one-off fetched SNAPSHOT over the live store
//   copy, so status/confirmations froze until a refresh. It now shows the
//   freshest copy and also listens for live updates for this exact tx.
// - Re-fetches silently when the socket reconnects (phone woke up / network
//   came back) so missed updates are caught up.
// - The full-page spinner only shows when there is nothing to display yet.
// - Step mapping comes from STATUS_CONFIG (single source of truth).

// ─── Small helpers (no ternaries in JSX) ───────────────────────────────────────

function pickFirst(condition, whenTrue, whenFalse) {
  if (condition) return whenTrue;
  return whenFalse;
}

function getSocketForRole(admin) {
  if (admin) return getAdminSocket();
  return getClientSocket();
}

function pickFreshest(primary, secondary) {
  if (!primary) return secondary || null;
  if (!secondary) return primary;
  if (isIncomingTxFresh(primary, secondary)) return { ...primary, ...secondary };
  return primary;
}

// ─── Sub-components ─────────────────────────────────────────────────────────────

function SectionCard({ title, icon, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.25 },
        borderRadius: 3,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        height: '100%',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
          }}
        >
          <Iconify icon={icon} width={18} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Paper>
  );
}

SectionCard.propTypes = {
  children: PropTypes.node.isRequired,
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

function CopyButton({ label, text, onCopy, copied }) {
  return (
    <Tooltip title={pickFirst(copied, 'Copied!', label)}>
      <IconButton
        size="small"
        onClick={() => onCopy(text, label)}
        color={pickFirst(copied, 'success', 'default')}
        sx={{ width: 30, height: 30, borderRadius: 2 }}
      >
        <Iconify icon={pickFirst(copied, 'mdi:check', 'mdi:content-copy')} width={16} />
      </IconButton>
    </Tooltip>
  );
}

CopyButton.propTypes = {
  copied: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onCopy: PropTypes.func.isRequired,
  text: PropTypes.string,
};

function ExternalLinkButton({ href, title }) {
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ width: 30, height: 30, borderRadius: 2 }}
      >
        <Iconify icon="mdi:open-in-new" width={16} />
      </IconButton>
    </Tooltip>
  );
}

ExternalLinkButton.propTypes = {
  href: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

function InfoRow({ label, value, mono = false, action = null }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 1.1 }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: pickFirst(mono, 'monospace', 'inherit'),
            fontWeight: 600,
            minWidth: 0,
            textAlign: 'right',
            wordBreak: pickFirst(mono, 'break-all', 'normal'),
          }}
        >
          {value || '-'}
        </Typography>
        {action}
      </Stack>
    </Stack>
  );
}

InfoRow.propTypes = {
  action: PropTypes.node,
  label: PropTypes.string.isRequired,
  mono: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function SummaryTile({ label, amount, token, icon, accentColor }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        borderRadius: 3,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        height: '100%',
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: (theme) => alpha(accentColor || theme.palette.primary.main, 0.1),
            color: accentColor || 'primary.main',
            flexShrink: 0,
          }}
        >
          <Iconify icon={icon} width={20} />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
            {token}
          </Typography>
        </Box>
      </Stack>
      <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05 }}>
        {formatAmount(amount)}
      </Typography>
    </Paper>
  );
}

SummaryTile.propTypes = {
  accentColor: PropTypes.string,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  token: PropTypes.string,
};

const TransactionStepIconContext = createContext({ isFailed: false, currentStepIndex: 0 });

function getStepIconBackground(theme, isFilled, dotColor) {
  if (isFilled) return dotColor;

  let restAlpha = 0.05;
  if (theme.palette.mode === 'dark') {
    restAlpha = 0.08;
  }

  return alpha(theme.palette.text.primary, restAlpha);
}

function TransactionStepIcon(props) {
  const { active, completed, icon } = props;
  const theme = useTheme();
  const { isFailed, currentStepIndex } = useContext(TransactionStepIconContext);

  const stepIndex = Number.parseInt(icon, 10) - 1;
  const stepStatusKey = STEP_ORDER[stepIndex];
  const config = STATUS_CONFIG[stepStatusKey] ?? STATUS_CONFIG.pending;

  let iconName = config.icon;
  let dotColor = theme.palette[config.color]?.main || theme.palette.text.secondary;
  let showSpinner = false;

  if (isFailed && stepIndex >= currentStepIndex) {
    iconName = 'mdi:alert-circle';
    dotColor = theme.palette.error.main;
  } else if (active && config.spinner) {
    showSpinner = true;
    dotColor = theme.palette.primary.main;
  } else if (completed || stepIndex < currentStepIndex) {
    iconName = 'mdi:check-circle';
    dotColor = theme.palette.success.main;
  } else if (active || stepIndex === currentStepIndex) {
    dotColor = theme.palette.primary.main;
  }

  const isFilled = Boolean(active || completed);

  let boxShadow;
  if (active) {
    boxShadow = `0 0 0 4px ${alpha(dotColor, 0.18)}`;
  }

  function renderStepGlyph() {
    if (showSpinner) {
      return <CircularProgress size={20} color="inherit" thickness={5} />;
    }

    return <Iconify icon={iconName} width={20} />;
  }

  return (
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: getStepIconBackground(theme, isFilled, dotColor),
        color: pickFirst(isFilled, '#fff', dotColor),
        boxShadow,
        transition: 'all 0.2s ease',
      }}
    >
      {renderStepGlyph()}
    </Box>
  );
}

TransactionStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
  icon: PropTypes.node,
};

// ─── Formatting helpers ───────────────────────────────────────────────────────

function getEtherscanUrl(hash) {
  return `${globalConfig.etherscanEndPoint}/tx/${hash}`;
}

function formatTimestamp(timestamp) {
  let date = timestamp?.$date;
  if (typeof timestamp === 'string') {
    date = timestamp;
  }

  if (!date) return '';

  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatHash(hash) {
  if (!hash) return '-';

  return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
}

function formatPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');

  if (!digits) return '-';

  if (/^252\d{9}$/.test(digits)) {
    return `+252 ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }

  if (/^\d{9}$/.test(digits)) {
    return `+252 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
  }

  return phone;
}

function getUserIdText(txData) {
  const raw = String(txData?.userId?._id ?? txData?.userId?.$oid ?? txData?.userId ?? '');

  if (!raw) return '-';
  return `${raw.slice(0, 8)}...`;
}

function getCardShadow(theme) {
  if (theme.palette.mode === 'light') return '0 20px 40px rgba(16, 24, 40, 0.08)';
  return '0 20px 40px rgba(0, 0, 0, 0.28)';
}

// ─── Receipt ──────────────────────────────────────────────────────────────────

function getReceiptReference(txData) {
  return String(txData?._id || '')
    .slice(-10)
    .toUpperCase();
}

function buildReceiptPayload(txData, theme) {
  const status = STATUS_CONFIG[txData?.status] ?? STATUS_CONFIG.pending;
  const reference = getReceiptReference(txData);
  const issuedAt = formatTimestamp(new Date().toISOString());

  return {
    brandName: 'SafiSwap',
    title: 'Transaction receipt',
    reference,
    issuedAt,
    brandColor: theme.palette.primary.main,
    status: {
      label: status.label,
      color: theme.palette[status.color]?.main,
    },
    headline: [
      {
        label: 'You sent',
        amount: String(formatAmount(txData?.amountIn) ?? '-'),
        token: String(txData?.tokenIn || '').toUpperCase(),
      },
      {
        label: 'Recipient gets',
        amount: String(formatAmount(txData?.amountOut) ?? '-'),
        token: String(txData?.tokenOut || '').toUpperCase(),
      },
    ],
    sections: [
      {
        title: 'Transaction',
        rows: [
          { label: 'Status', value: status.label },
          { label: 'Created', value: formatTimestamp(txData?.createdAt) },
        ],
      },
      {
        title: 'Recipient',
        rows: [
          { label: 'Recipient name', value: txData?.name },
          { label: 'Recipient phone', value: formatPhone(txData?.recipientPhone) },
          { label: 'Sender phone', value: formatPhone(txData?.phoneNumber) },
        ],
      },
    ],
    footerNote: `Generated ${issuedAt}. This is a computer-generated receipt and does not require a signature. Keep the reference number for any support request.`,
    fileName: `SafiSwap-receipt-${reference || 'transaction'}.pdf`,
  };
}

// ─── Main export ─────────────────────────────────────────────────────────────
export default function TransactionStatusCard({ id, admin, transaction: txProp }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { isUserAuthenticated, isAdminAuthenticated } = useContext(AuthContext);

  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [fetchAttemptedId, setFetchAttemptedId] = useState(null);
  const [fetchedTransaction, setFetchedTransaction] = useState(null);
  const [isGeneratingReceipt, setIsGeneratingReceipt] = useState(false);

  const [copiedItem, setCopiedItem] = useState(null);
  const timeoutRef = useRef(null);
  const isFetchingRef = useRef(false);

  const lookupId = id ?? txProp?._id ?? null;

  const txFromStore = useSelector((state) => {
    if (!lookupId) return null;

    let slice = state?.transactions;
    if (admin) {
      slice = state?.adminTransactions;
    }

    return slice?.items?.find((t) => String(t?._id) === String(lookupId)) ?? null;
  });

  const canFetchTransaction = isAdminAuthenticated || isUserAuthenticated;

  const fetchTransaction = useCallback(
    async ({ force = false, silent = false } = {}) => {
      if (!id || !canFetchTransaction) return;
      if (isFetchingRef.current && !force) return;

      isFetchingRef.current = true;

      if (!silent) {
        setIsFetching(true);
        setFetchError(null);
      }

      setFetchAttemptedId(id);

      try {
        const result = await dispatch(fetchTransactionById(id));

        if (fetchTransactionById.rejected.match(result)) {
          if (!silent) {
            setFetchError(result.payload || 'Failed to load transaction');
          }
          return;
        }

        const payload = result.payload?.transaction ?? result.payload?.data ?? result.payload;

        if (payload) {
          setFetchedTransaction((prev) => pickFreshest(prev, payload));
        }
      } catch (err) {
        console.error('Failed to fetch transaction:', err);

        if (!silent) {
          setFetchError(err?.message || 'Failed to load transaction');
        }
      } finally {
        isFetchingRef.current = false;
        setIsFetching(false);
      }
    },
    [id, canFetchTransaction, dispatch]
  );

  useEffect(() => {
    if (!id || !canFetchTransaction) return;
    if (fetchAttemptedId === id) return;

    fetchTransaction();
  }, [id, canFetchTransaction, fetchAttemptedId, fetchTransaction]);

  // Navigating from one transaction to another: drop the previous copy.
  const previousIdRef = useRef(id);
  useEffect(() => {
    if (previousIdRef.current === id) return;
    previousIdRef.current = id;

    setFetchError(null);
    setFetchedTransaction(null);
    setFetchAttemptedId(null);
  }, [id]);

  // Live updates for THIS transaction, even when it is not in the list store
  // (direct link / hard refresh / admin opening an older tx).
  const fetchTransactionRef = useRef(fetchTransaction);
  useEffect(() => {
    fetchTransactionRef.current = fetchTransaction;
  }, [fetchTransaction]);

  useEffect(() => {
    if (!lookupId) return undefined;

    const socket = getSocketForRole(admin);

    const handleUpdate = (payload) => {
      const incoming = payload?.transaction;
      if (!incoming?._id) return;
      if (String(incoming._id) !== String(lookupId)) return;

      setFetchedTransaction((prev) => pickFreshest(prev, incoming));
    };

    // After a reconnect we may have missed updates — catch up quietly.
    const handleReconnect = () => {
      fetchTransactionRef.current?.({ force: true, silent: true });
    };

    socket.on('updateTransaction:update', handleUpdate);
    socket.io?.on?.('reconnect', handleReconnect);

    return () => {
      socket.off('updateTransaction:update', handleUpdate);
      socket.io?.off?.('reconnect', handleReconnect);
    };
  }, [lookupId, admin]);

  let fetchedTransactionForCurrentId = null;
  if (fetchedTransaction && String(fetchedTransaction?._id) === String(lookupId)) {
    fetchedTransactionForCurrentId = fetchedTransaction;
  }

  const transaction = pickFreshest(txFromStore, fetchedTransactionForCurrentId) ?? txProp ?? null;

  const handleRetry = async () => {
    setFetchError(null);
    setFetchAttemptedId(null);

    await fetchTransaction({ force: true });
  };

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const copyToClipboard = async (text, label) => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);

      setCopiedItem(label);
      toast.success(`${label} copied to clipboard`);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => setCopiedItem(null), 1800);
    } catch (err) {
      console.error('Copy failed', err);
      toast.error('Could not copy to clipboard');
    }
  };

  const txData = transaction;

  const handleDownloadReceipt = async () => {
    if (!txData || isGeneratingReceipt) return;

    setIsGeneratingReceipt(true);

    try {
      await downloadReceiptPdf(buildReceiptPayload(txData, theme));
      toast.success('Receipt downloaded');
    } catch (err) {
      console.error('Receipt generation failed', err);
      toast.error('Could not generate receipt');
    } finally {
      setIsGeneratingReceipt(false);
    }
  };

  const currentStepIndex = getStepIndexFromStatus(txData?.status);
  const isCompleted = txData?.status === 'sent';
  const isFailed = isFailedStatus(txData?.status);

  const currentStatus = STATUS_CONFIG[txData?.status] ?? STATUS_CONFIG.pending;

  const hasFiatPayoutInfo =
    txData?.recipientPhone ||
    txData?.payoutAttemptCount != null ||
    txData?.payoutAttemptedAt ||
    txData?.payoutFailedAt ||
    txData?.fiatPayoutProviderTxId;

  const confirmationText = useMemo(() => {
    const progress = getConfirmationProgress(txData);
    if (!progress) return null;
    return `${progress} confirmations`;
  }, [txData]);

  const stepIconContextValue = useMemo(
    () => ({ isFailed, currentStepIndex }),
    [isFailed, currentStepIndex]
  );

  if (isFetching && !transaction) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError && !transaction) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" minHeight={300} gap={2} mt={6}>
        <Typography variant="body1" color="error">
          {fetchError}
        </Typography>

        <Button variant="outlined" onClick={handleRetry}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!transaction) {
    return <TransactionNotFound />;
  }

  function renderConfirmationChip() {
    if (!confirmationText) return null;

    return (
      <Chip
        size="small"
        label={confirmationText}
        sx={{
          height: 26,
          borderRadius: 999,
          fontWeight: 800,
          bgcolor: alpha(theme.palette.info.main, 0.1),
          color: 'info.main',
        }}
      />
    );
  }

  function renderUserChip() {
    if (!transaction?.userId?.name || !isAdminAuthenticated) return null;

    return (
      <Chip
        icon={<Iconify icon="solar:user-bold" width={16} />}
        label={transaction.userId.name}
        variant="outlined"
        sx={{ borderRadius: 2.5 }}
      />
    );
  }

  function renderStatusIcon() {
    if (currentStatus.spinner) {
      return <CircularProgress size={16} color="inherit" thickness={5} />;
    }

    return <Iconify icon={currentStatus.icon} width={16} />;
  }

  function renderReceiptButtonIcon() {
    if (isGeneratingReceipt) {
      return <CircularProgress size={16} color="inherit" thickness={5} />;
    }

    return <Iconify icon="solar:download-minimalistic-bold" width={18} />;
  }

  function renderReceiptButton() {
    return (
      <Button
        variant="contained"
        color="inherit"
        onClick={handleDownloadReceipt}
        disabled={isGeneratingReceipt}
        startIcon={renderReceiptButtonIcon()}
        sx={{ height: 34, borderRadius: 2.5, fontWeight: 800, textTransform: 'none' }}
      >
        Receipt
      </Button>
    );
  }

  function renderOptionalRow(value, label, rowValue) {
    if (!value) return null;

    return (
      <>
        <Divider />
        <InfoRow label={label} value={rowValue} />
      </>
    );
  }

  function renderProviderTxRow() {
    if (!txData?.fiatPayoutProviderTxId) return null;

    return (
      <>
        <Divider />
        <InfoRow
          label="Provider TX ID"
          value={txData.fiatPayoutProviderTxId}
          mono
          action={
            <CopyButton
              label="Provider TX ID"
              text={txData.fiatPayoutProviderTxId}
              onCopy={copyToClipboard}
              copied={copiedItem === 'Provider TX ID'}
            />
          }
        />
      </>
    );
  }

  function renderFiatPayoutSection() {
    if (!hasFiatPayoutInfo) return null;

    return (
      <SectionCard title="Fiat payout" icon="solar:card-send-bold">
        <InfoRow label="Receiver phone" value={formatPhone(txData?.recipientPhone)} />

        {renderOptionalRow(
          txData?.payoutAttemptedAt,
          'Payout attempted',
          formatTimestamp(txData?.payoutAttemptedAt)
        )}

        {renderOptionalRow(
          txData?.payoutFailedAt,
          'Payout failed',
          formatTimestamp(txData?.payoutFailedAt)
        )}

        {renderProviderTxRow()}
      </SectionCard>
    );
  }

  function renderInputHashRow() {
    if (!txData?.txHashIn) return null;

    let divider = null;
    if (txData?.txHashOut) {
      divider = <Divider />;
    }

    return (
      <>
        <InfoRow
          label="Input transaction"
          value={formatHash(txData?.txHashIn)}
          mono
          action={
            <Stack direction="column" spacing={0.5}>
              <CopyButton
                label="Input TX Hash"
                text={txData?.txHashIn}
                onCopy={copyToClipboard}
                copied={copiedItem === 'Input TX Hash'}
              />

              <ExternalLinkButton
                title="View on Etherscan"
                href={getEtherscanUrl(txData?.txHashIn)}
              />
            </Stack>
          }
        />

        {divider}
      </>
    );
  }

  function renderOutputHashRow() {
    if (!txData?.txHashOut) {
      return (
        <Typography variant="body2" color="text.secondary">
          No on-chain hash available yet.
        </Typography>
      );
    }

    return (
      <InfoRow
        label="Output transaction"
        value={formatHash(txData?.txHashOut)}
        mono
        action={
          <Stack direction="row" spacing={0.5}>
            <CopyButton
              label="Output TX Hash"
              text={txData?.txHashOut}
              onCopy={copyToClipboard}
              copied={copiedItem === 'Output TX Hash'}
            />

            <ExternalLinkButton
              title="View on Etherscan"
              href={getEtherscanUrl(txData?.txHashOut)}
            />
          </Stack>
        }
      />
    );
  }

  function renderErrorDetails() {
    if (!(txData?.error || txData?.refundError) || !isAdminAuthenticated) return null;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
          bgcolor: alpha(theme.palette.error.main, 0.05),
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75, color: 'error.main' }}>
          Error details
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          {txData?.refundError || txData?.error}
        </Typography>
      </Paper>
    );
  }

  let activeStep = currentStepIndex;
  if (isFailed) {
    activeStep = -1;
  }

  let connectorColor = alpha(theme.palette.primary.main, 0.3);
  if (isCompleted) {
    connectorColor = theme.palette.success.main;
  }

  return (
    <Card
      elevation={0}
      sx={{
        maxWidth: 1100,
        mx: 1,
        mt: 4,
        mb: 3,
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        boxShadow: getCardShadow(theme),
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2.25, sm: 2.75 },
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.primary.main,
            0.05
          )} 0%, transparent 100%)`,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Box
            sx={{
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: { sm: 'center', md: 'flex-start' },
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 1.1 }}
            >
              Transaction details
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ mt: 0.25 }}
            >
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.08 }}
              >
                {txData?.tokenIn?.toUpperCase()} → {txData?.tokenOut?.toUpperCase()}
              </Typography>

              {renderConfirmationChip()}
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 0.75, sm: 1.5 }}
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Button
                variant="text"
                color="inherit"
                onClick={() => copyToClipboard(txData._id, 'Transaction ID')}
                startIcon={<Iconify icon="solar:copy-bold" width={16} />}
                sx={{
                  px: 1,
                  py: 0.5,
                  minWidth: 0,
                  borderRadius: 2,
                  textTransform: 'none',
                  color: 'text.secondary',
                  fontFamily: 'monospace',
                  justifyContent: 'flex-start',
                }}
              >
                {txData._id?.slice(0, 8)}...{txData._id?.slice(-4)}
              </Button>

              <Typography variant="body2" color="text.secondary">
                {formatTimestamp(txData?.createdAt)}
              </Typography>
            </Stack>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
          >
            {renderUserChip()}

            <Chip
              icon={renderStatusIcon()}
              label={currentStatus.label}
              color={currentStatus.color}
              sx={{ height: 34, borderRadius: 2.5, fontWeight: 800 }}
            />

            {renderReceiptButton()}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.default, 0.72),
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2.5, letterSpacing: '-0.02em' }}>
            Transaction progress
          </Typography>

          <TransactionStepIconContext.Provider value={stepIconContextValue}>
            <Stepper
              activeStep={activeStep}
              alternativeLabel={!isMobile}
              orientation={pickFirst(isMobile, 'vertical', 'horizontal')}
              sx={{
                '& .MuiStepConnector-line': {
                  borderColor: connectorColor,
                  borderTopWidth: 3,
                },
              }}
            >
              {STEP_ORDER.map((statusKey, index) => {
                const config = STATUS_CONFIG[statusKey];
                const isActive = index === currentStepIndex;
                const isComplete = index < currentStepIndex || isCompleted;

                return (
                  <Step key={statusKey} completed={isComplete && !isFailed}>
                    <StepLabel StepIconComponent={TransactionStepIcon}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: pickFirst(isActive, 800, 600),
                          color: pickFirst(isActive, 'text.primary', 'text.secondary'),
                        }}
                      >
                        {config.label}
                      </Typography>
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </TransactionStepIconContext.Provider>
        </Paper>

        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack spacing={2.5}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <SummaryTile
                    label="From"
                    amount={txData?.amountIn}
                    token={txData?.tokenIn?.toUpperCase()}
                    icon="solar:arrow-down-bold"
                    accentColor={theme.palette.primary.main}
                  />
                </Grid>

                <Grid size={12}>
                  <SummaryTile
                    label="To"
                    amount={txData?.amountOut}
                    token={txData?.tokenOut?.toUpperCase()}
                    icon="solar:arrow-up-bold"
                    accentColor={theme.palette.success.main}
                  />
                </Grid>
              </Grid>

              <SectionCard title="Additional information" icon="solar:document-text-bold">
                <InfoRow label="Intent ID" value={formatHash(txData?.intentId)} mono />
                <Divider />

                <InfoRow label="User ID" value={getUserIdText(txData)} mono />
                <Divider />

                <InfoRow label="Sender phone" value={formatPhone(txData?.phoneNumber)} />
                <Divider />

                <InfoRow label="Recipient phone" value={formatPhone(txData?.recipientPhone)} />
                <Divider />

                <InfoRow label="Recipient name" value={txData?.name} />
                <Divider />

                <InfoRow label="Last updated" value={formatTimestamp(txData?.updatedAt)} />
                <Divider />
              </SectionCard>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={2.5}>
              <SectionCard title="Addresses" icon="solar:wallet-bold">
                <InfoRow
                  label="From"
                  value={formatAddress(txData?.from)}
                  mono
                  action={
                    <CopyButton
                      label="From Address"
                      text={txData?.from}
                      onCopy={copyToClipboard}
                      copied={copiedItem === 'From Address'}
                    />
                  }
                />

                <Divider />

                <InfoRow
                  label="To"
                  value={formatAddress(txData?.to)}
                  mono
                  action={
                    <CopyButton
                      label="To Address"
                      text={txData?.to}
                      onCopy={copyToClipboard}
                      copied={copiedItem === 'To Address'}
                    />
                  }
                />
              </SectionCard>

              {renderFiatPayoutSection()}

              <SectionCard title="Transaction hashes" icon="solar:link-bold">
                {renderInputHashRow()}

                {renderOutputHashRow()}
              </SectionCard>

              {renderErrorDetails()}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
}

TransactionStatusCard.propTypes = {
  id: PropTypes.string,
  admin: PropTypes.bool,
  transaction: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    intentId: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string,
    recipientPhone: PropTypes.string,
    amountIn: PropTypes.string.isRequired,
    amountOut: PropTypes.string.isRequired,
    amountRefunded: PropTypes.string,
    depositConfirmations: PropTypes.number,
    payoutConfirmations: PropTypes.number,
    payoutAttemptCount: PropTypes.number,
    payoutAttemptedAt: PropTypes.oneOfType([
      PropTypes.shape({ $date: PropTypes.string.isRequired }),
      PropTypes.string,
    ]),
    payoutFailedAt: PropTypes.oneOfType([
      PropTypes.shape({ $date: PropTypes.string.isRequired }),
      PropTypes.string,
    ]),
    fiatPayoutProviderTxId: PropTypes.string,
    createdAt: PropTypes.oneOfType([
      PropTypes.shape({ $date: PropTypes.string.isRequired }),
      PropTypes.string,
    ]).isRequired,
    updatedAt: PropTypes.oneOfType([
      PropTypes.shape({ $date: PropTypes.string.isRequired }),
      PropTypes.string,
    ]).isRequired,
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
    status: PropTypes.oneOf([
      'pending',
      'deposit_processing',
      'deposit_seen',
      'deposit_confirmed',
      'payout_queued',
      'payout_processing',
      'sent',
      'payout_failed',
      'payout_timed_out',
      'deposit_failed',
      'refunded',
      'refund_failed',
      'underpaid_refund_pending',
      'overpaid_refund_pending',
    ]).isRequired,
    tokenIn: PropTypes.string.isRequired,
    tokenOut: PropTypes.string.isRequired,
    txHashIn: PropTypes.string,
    txHashOut: PropTypes.string,
    slippage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userId: PropTypes.any,
    error: PropTypes.string,
    refundError: PropTypes.string,
  }),
};
